import { cn } from 'lib/utils';
import { CheckCircle, Copy } from 'lucide-react';
import type { ComponentProps } from 'react';
import { forwardRef, useState } from 'react';
import { toast } from 'sonner';

interface Props extends Omit<ComponentProps<'div'>, 'onClick' | 'title'> {
  text: string;
  copyText: string;
}

const ClickToCopyButton = forwardRef<HTMLDivElement, Props>(({ text, copyText, className, ...rest }, ref) => {
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    navigator.clipboard?.writeText(copyText).then(() => {
      toast.info(`Copied to clipboard:\n${copyText}`);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    });
  };

  const title = copied ? 'Copied' : 'Click to copy to clipboard';

  return (
    <div
      {...rest}
      ref={ref}
      role="button"
      className={cn(
        'flex w-full flex-none items-center py-3 sm:w-auto',
        'leading-6 text-gray-400 transition-colors duration-200 hover:text-gray-900',
        'cursor-pointer',
        className,
      )}
      onClick={onClick}
      title={title}
    >
      <span className="">{text}</span>
      <span className="sr-only">(click to copy to clipboard)</span>
      <div className="ml-2">{copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</div>
    </div>
  );
});
ClickToCopyButton.displayName = 'ClickToCopyButton';

export default ClickToCopyButton;
